import React from 'react';
import { Container, Alert, Spinner, Card } from 'react-bootstrap';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { useWeb3Context } from '../context/Web3Context';
import { useWallet } from '@solana/wallet-adapter-react';
import Header from './Header';
import Footer from './Footer';

/**
 * ProtectedAdminRoute - Protects admin routes by checking if user has admin privileges
 * Redirects non-admin users to home page and shows appropriate messages
 */
const ProtectedAdminRoute = ({ children }) => {
  const { isAdmin, isLoading, hasWalletConnected } = useAdminAuth();
  const { address: bscAddress, connect } = useWeb3Context();
  const { connect: connectSolana, publicKey } = useWallet();

  // Show loading spinner while checking admin status
  if (isLoading) {
    return (
      <div>
        <Header />
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Verifying admin access...</p>
          </div>
        </Container>
        <Footer />
      </div>
    );
  }

  // If no wallet is connected, show connection prompt
  if (!hasWalletConnected) {
    return (
      <div>
        <Header />
        <Container className="py-5">
          <Card className="text-center">
            <Card.Body>
              <Card.Title>Admin Access Required</Card.Title>
              <Alert variant="warning">
                <Alert.Heading>🔐 Authentication Required</Alert.Heading>
                <p>
                  You need to connect an admin wallet to access this page.
                  Please connect your MetaMask (BSC) or Solana wallet.
                </p>
                <hr />
                <div className="d-grid gap-2 d-md-flex justify-content-md-center">
                  <button 
                    className="btn btn-primary me-md-2" 
                    onClick={connect}
                  >
                    Connect MetaMask (BSC)
                  </button>
                  <button 
                    className="btn btn-secondary" 
                    onClick={connectSolana}
                  >
                    Connect Solana Wallet
                  </button>
                </div>
              </Alert>
            </Card.Body>
          </Card>
        </Container>
        <Footer />
      </div>
    );
  }

  // If wallet is connected but user is not admin, show access denied
  if (hasWalletConnected && !isAdmin) {
    return (
      <div>
        <Header />
        <Container className="py-5">
          <Card className="text-center">
            <Card.Body>
              <Card.Title>Access Denied</Card.Title>
              <Alert variant="danger">
                <Alert.Heading>🚫 Admin Access Required</Alert.Heading>
                <p>
                  Your wallet address is not authorized to access the admin dashboard.
                  Only designated admin wallets can access this area.
                </p>
                <hr />
                <p className="mb-0">
                  <strong>Connected Wallet:</strong>
                  <br />
                  {bscAddress && <span className="text-muted">BSC: {bscAddress}</span>}
                  {bscAddress && publicKey && <br />}
                  {publicKey && <span className="text-muted">Solana: {publicKey.toString()}</span>}
                </p>
                <hr />
                <div className="d-grid gap-2 d-md-flex justify-content-md-center">
                  <button 
                    className="btn btn-primary me-md-2" 
                    onClick={() => window.location.href = '/'}
                  >
                    Go to Home
                  </button>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => window.location.href = '/bridge'}
                  >
                    Go to Bridge
                  </button>
                </div>
              </Alert>
            </Card.Body>
          </Card>
        </Container>
        <Footer />
      </div>
    );
  }

  // If user is admin, render the protected component
  return children;
};

export default ProtectedAdminRoute;
